import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Flex,
  Group,
  Rating,
  SimpleGrid,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import {
  IconBrandInstagram,
  IconBrandTiktok,
  IconBrandTwitter,
  IconBrandYoutube,
  IconCash,
  IconTag,
  IconUsers,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { User } from "../../../../gql/__generated__/graphql";
import CollabModal, {
  BrandCollabFormValues,
  CreatorCollabFormValues,
} from "../../../pages/DiscoverPage/components/CollabModal";

export interface SocialMedia {
  tiktok?: number;
  instagram?: number;
  youtube?: number;
  twitter?: number;
}

export interface CreatorSummary {
  id: string;
  name: string;
  image: string;
  rating: number;
  description: string;
  monthlyGMV?: number;
  socialMedia: SocialMedia;
  categories: string[];
  completedCollabs: number;
}

interface CreatorCardProps {
  creator: User;
  products?: any[]; // Products passed from parent
}

function CreatorCard({ creator, products = [] }: CreatorCardProps) {
  const theme = useMantineTheme();

  // React State
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Memoize the total followers calculation
  const totalFollowers = useMemo(() => {
    return creator.socialMedia
      ? Object.entries(creator.socialMedia)
          .filter(([key]) => key !== "__typename")
          .reduce((sum, [_, count]) => (sum || 0) + (count || 0), 0)
      : 0;
  }, [creator.socialMedia]);

  // Format follower counts (e.g., 1500 -> 1.5K)
  function formatFollowers(count: number): string {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    }
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }

    return count.toString();
  }

  // Format currency (e.g., 15000 -> $15,000)
  function formatCurrency(amount: number): string {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(amount);
  }

  // Get icon and color for each social platform
  function getSocialIcon(platform: string) {
    switch (platform) {
      case "twitter":
        return { icon: <IconBrandTwitter size={18} />, color: "#1DA1F2" };
      case "instagram":
        return { icon: <IconBrandInstagram size={18} />, color: "#C13584" };
      case "youtube":
        return { icon: <IconBrandYoutube size={18} />, color: "#FF0000" };
      case "tiktok":
        return { icon: <IconBrandTiktok size={18} />, color: "#000000" };
      default:
        return { icon: <IconUsers size={18} />, color: theme.colors.blue[6] };
    }
  }

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Flex
        direction={{ base: "column", sm: "row" }}
        gap="md"
        align={{ base: "center", sm: "flex-start" }}
      >
        {/* Avatar */}
        <Avatar
          src={creator.imageUrl}
          size={80}
          radius={80}
          alt={`${creator.name}'s profile picture`}
        />

        {/* Creator info */}
        <Stack gap="xs" style={{ flex: 1 }}>
          <Group justify="space-between" wrap="nowrap">
            <Box>
              <Text fw={700} size="xl">
                {creator.name}
              </Text>
              <Group gap="xs">
                <Rating value={creator.rating || 0} readOnly fractions={2} />
                <Text size="sm" c="dimmed">
                  ({(creator.rating || 0).toFixed(1)})
                </Text>
              </Group>
            </Box>

            {/* Total follower badge - visible on mobile and desktop */}
            {totalFollowers > 0 && (
              <Badge
                size="lg"
                variant="light"
                leftSection={<IconUsers size={14} />}
                styles={{
                  root: {
                    whiteSpace: "nowrap",
                    minWidth: "fit-content",
                    overflow: "visible",
                  },
                  label: {
                    overflow: "visible",
                    textOverflow: "clip",
                  },
                }}
              >
                {formatFollowers(totalFollowers)}
              </Badge>
            )}
          </Group>

          <Text size="sm" lineClamp={3}>
            {creator.description || "No description available"}
          </Text>
        </Stack>
      </Flex>

      {/* Categories and Monthly GMV */}
      <SimpleGrid cols={2} mt="md">
        <Group gap="xs">
          {creator.categories?.map((category) => (
            <Badge
              key={category}
              variant="outline"
              size="sm"
              leftSection={<IconTag size={12} />}
            >
              {category}
            </Badge>
          )) || null}
        </Group>

        {creator.monthlyGMV && (
          <Badge
            size="lg"
            color="green"
            variant="light"
            leftSection={<IconCash size={14} />}
          >
            {formatCurrency(creator.monthlyGMV)}/mo GMV
          </Badge>
        )}
      </SimpleGrid>

      <Divider my="md" />

      {/* Social Media Section */}
      {/* <Flex gap="md" wrap="wrap" justify={{ base: "center", sm: "flex-start" }}> */}
      <Flex gap="md" wrap="wrap" justify={"space-between"}>
        {/* Display each social media platform with followers */}
        {/* TODO: LATER, re-enable this */}
        {/* {creator.socialMedia &&
          Object.entries(creator.socialMedia).map(
            ([platform, followers]) =>
              followers &&
              followers > 0 && (
                <Group key={platform} gap="xs">
                  <ActionIcon
                    variant="light"
                    color={getSocialIcon(platform).color}
                    size="lg"
                    radius="xl"
                  >
                    {getSocialIcon(platform).icon}
                  </ActionIcon>
                  <Stack gap={0}>
                    <Text size="xs" tt="uppercase" fw={500}>
                      {platform}
                    </Text>
                    <Text size="sm" fw={700}>
                      {formatFollowers(followers)}
                    </Text>
                  </Stack>
                </Group>
              )
          )} */}
        {creator.socialMedia?.tiktok && creator.socialMedia.tiktok > 0 && (
          <Group key="tiktok" gap="xs">
            <ActionIcon
              variant="light"
              color={getSocialIcon("tiktok").color}
              size="lg"
              radius="xl"
            >
              {getSocialIcon("tiktok").icon}
            </ActionIcon>
            <Stack gap={0}>
              <Text size="xs" tt="uppercase" fw={500}>
                tiktok
              </Text>
              <Text size="sm" fw={700}>
                {formatFollowers(creator.socialMedia.tiktok)}
              </Text>
            </Stack>
          </Group>
        )}

        <Button
          onClick={() => {
            setIsModalOpen(true);
          }}
        >
          Collab
        </Button>
      </Flex>

      {/* <Group pt={"xs"} justify="flex-end">
        <Button
          onClick={() => {
            setIsModalOpen(true);
          }}
        >
          Collab
        </Button>
      </Group> */}

      <CollabModal
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        isBrand={true}
        creator={creator}
        products={products}
        onSubmit={function (
          values: BrandCollabFormValues | CreatorCollabFormValues
        ): void {
          console.log("successful collab sent");
        }}
      />
    </Card>
  );
}

export default CreatorCard;
